import type { ReplayFxEventDetail, ReplayFxEventType } from '../replayFx'

export type ReplayFxTimelineTone = 'cyan' | 'white' | 'amber' | 'red'
export type ReplayFxTimelineTrack = 'LOAD' | 'SYSTEM' | 'TRANSITION' | 'GAMEPLAY'
export type ReplayFxTimelineBusId = 'story' | 'runtime' | 'interface' | 'effects' | 'gameplay'
export type ReplayFxTimelineClipKind = 'phase' | 'ambient' | 'guide' | 'effect' | 'measured'

export type ReplayFxTimelineLiveMarker = {
  detail: ReplayFxEventDetail
  elapsedMs: number
}

export type ReplayFxTimelineActivity = {
  type: string
  time: number
  duration?: number
}

export type ReplayFxStoryPhase = {
  order: number
  minDurationMs: number
  waitFor?: ReplayFxEventType
}

type ReplayFxLiveStoryPhaseInput = {
  id: string
  label: string
  laneId: string
  tone: ReplayFxTimelineTone
  startMs: number
  endMs: number
  phase: ReplayFxStoryPhase
}

export type ReplayFxTimelineLane = {
  id: string
  label: string
}

export type ReplayFxTimelineBus = {
  id: ReplayFxTimelineBusId
  label: string
  lanes: ReplayFxTimelineLane[]
}

export type ReplayFxTimelineCueDefinition = {
  id: string
  label: string
  track: ReplayFxTimelineTrack
  tone: ReplayFxTimelineTone
  startBeat: number
  durationBeats: number
  detail: Omit<ReplayFxEventDetail, 'at' | 'durationMs'>
}

export type ReplayFxTimelineCue = Omit<ReplayFxTimelineCueDefinition, 'startBeat' | 'durationBeats' | 'detail'> & {
  kind: ReplayFxTimelineClipKind
  busId: ReplayFxTimelineBusId
  laneId: string
  startMs: number
  durationMs: number
  detail?: Omit<ReplayFxEventDetail, 'at' | 'durationMs'>
  phase?: ReplayFxStoryPhase
}

export const DEFAULT_REPLAY_FX_BEAT_MS = 960
export const REPLAY_FX_TIMELINE_LENGTH_BEATS = 48
export const REPLAY_FX_TIMELINE_STORAGE_KEY = 'replay-fx-timeline-v3'

export type ReplayFxStoryPhaseDefinition = {
  id: string
  label: string
  laneId: string
  tone: ReplayFxTimelineTone
  startBeat: number
  durationBeats: number
  minDurationMs: number
  waitFor?: ReplayFxEventType
}

export const STORY_PHASE_DEFINITIONS: ReplayFxStoryPhaseDefinition[] = [
  { id: 'story-boot', label: '01 TV POWER ON', laneId: 'boot', tone: 'white', startBeat: 0, durationBeats: 2, minDurationMs: 1920 },
  { id: 'story-file', label: '02 FILE INTAKE', laneId: 'file', tone: 'cyan', startBeat: 0.5, durationBeats: 4.5, minDurationMs: 4320, waitFor: 'file-ready' },
  { id: 'story-ui', label: '03 UI REVEAL', laneId: 'ui-reveal', tone: 'amber', startBeat: 4, durationBeats: 3, minDurationMs: 2880, waitFor: 'playback-start' },
  { id: 'story-world', label: '04 WORLD BUILD', laneId: 'world', tone: 'cyan', startBeat: 4.5, durationBeats: 2.5, minDurationMs: 2400, waitFor: 'startup-prefetch-ready' },
  { id: 'story-play', label: '05 CONTENT PLAY', laneId: 'play', tone: 'white', startBeat: 7, durationBeats: 38, minDurationMs: 0, waitFor: 'playback-end' },
]

type ReplayFxLivePhaseDefinition = {
  id: string
  label: string
  busId: ReplayFxTimelineBusId
  laneId: string
  tone: ReplayFxTimelineTone
  start: ReplayFxEventType
  end?: ReplayFxEventType
  durationFromWallMs?: boolean
}

const LIVE_PHASE_DEFINITIONS: ReplayFxLivePhaseDefinition[] = [
  { id: 'file', label: 'FILE LOAD', busId: 'runtime', laneId: 'file', tone: 'cyan', start: 'file-loading', end: 'file-ready' },
  { id: 'loading-cover', label: 'LOAD COVER', busId: 'interface', laneId: 'loading-ui', tone: 'cyan', start: 'file-loading', end: 'playback-start' },
  { id: 'world', label: 'WORLD PREFETCH', busId: 'runtime', laneId: 'world', tone: 'cyan', start: 'startup-prefetch-start', end: 'startup-prefetch-ready' },
  { id: 'gpu', label: 'GPU PREWARM', busId: 'runtime', laneId: 'gpu', tone: 'amber', start: 'prewarm-start', end: 'prewarm-ready' },
  { id: 'settle', label: 'VIEWER SETTLE', busId: 'interface', laneId: 'viewer', tone: 'amber', start: 'startup-settle', durationFromWallMs: true },
  { id: 'playback', label: 'CONTENT PLAYBACK', busId: 'runtime', laneId: 'clock', tone: 'white', start: 'playback-start', end: 'playback-end' },
  { id: 'hud', label: 'REPLAY HUD', busId: 'interface', laneId: 'hud', tone: 'white', start: 'playback-start', end: 'playback-end' },
  { id: 'seek', label: 'SEEK', busId: 'runtime', laneId: 'clock', tone: 'amber', start: 'seek-start', end: 'seek-ready' },
]

const LIVE_EVENT_PLACEMENT: Record<ReplayFxEventType, {
  label: string
  busId: ReplayFxTimelineBusId
  laneId: string
  tone: ReplayFxTimelineTone
}> = {
  'file-loading': { label: 'CRT BOOT', busId: 'effects', laneId: 'crt', tone: 'white' },
  'file-ready': { label: 'FILE READY', busId: 'effects', laneId: 'electric', tone: 'cyan' },
  'startup-prefetch-start': { label: 'PREFETCH', busId: 'effects', laneId: 'electric', tone: 'cyan' },
  'startup-prefetch-ready': { label: 'DATA READY', busId: 'effects', laneId: 'electric', tone: 'cyan' },
  'prewarm-start': { label: 'GPU PREWARM', busId: 'effects', laneId: 'electric', tone: 'amber' },
  'prewarm-ready': { label: 'GPU READY', busId: 'effects', laneId: 'electric', tone: 'white' },
  'startup-settle': { label: 'SETTLE', busId: 'effects', laneId: 'transition', tone: 'cyan' },
  'playback-start': { label: 'LAUNCH', busId: 'effects', laneId: 'transition', tone: 'white' },
  'playback-end': { label: 'END', busId: 'effects', laneId: 'transition', tone: 'amber' },
  'seek-start': { label: 'SEEK CUT', busId: 'effects', laneId: 'transition', tone: 'cyan' },
  'seek-ready': { label: 'SEEK READY', busId: 'effects', laneId: 'transition', tone: 'white' },
  damage: { label: 'IMPACT', busId: 'gameplay', laneId: 'events', tone: 'red' },
  clear: { label: 'CLEAR', busId: 'effects', laneId: 'crt', tone: 'white' },
}

export const REPLAY_FX_TIMELINE_BUSES: ReplayFxTimelineBus[] = [
  {
    id: 'story',
    label: 'STORY',
    lanes: [
      { id: 'boot', label: 'BOOT' },
      { id: 'file', label: 'FILE' },
      { id: 'ui-reveal', label: 'UI' },
      { id: 'world', label: 'WORLD' },
      { id: 'play', label: 'PLAY' },
      { id: 'idle', label: 'IDLE' },
    ],
  },
  {
    id: 'runtime',
    label: 'RUNTIME',
    lanes: [
      { id: 'app', label: 'APP' },
      { id: 'file', label: 'FILE' },
      { id: 'world', label: 'WORLD' },
      { id: 'gpu', label: 'GPU' },
      { id: 'clock', label: 'REPLAY' },
    ],
  },
  {
    id: 'interface',
    label: 'INTERFACE',
    lanes: [
      { id: 'shell', label: 'SHELL' },
      { id: 'loading-ui', label: 'LOADING UI' },
      { id: 'viewer', label: 'VIEWER' },
      { id: 'hud', label: 'HUD' },
    ],
  },
  {
    id: 'effects',
    label: 'VISUAL FX',
    lanes: [
      { id: 'crt', label: 'CRT / NOISE' },
      { id: 'electric', label: 'ELECTRIC' },
      { id: 'transition', label: 'TRANSITIONS' },
    ],
  },
  {
    id: 'gameplay',
    label: 'GAMEPLAY',
    lanes: [
      { id: 'events', label: 'EVENTS' },
    ],
  },
]

export const REPLAY_FX_CUE_DEFINITIONS: ReplayFxTimelineCueDefinition[] = [
  { id: 'boot', label: 'CRT BOOT', track: 'LOAD', tone: 'white', startBeat: 0, durationBeats: 5, detail: { type: 'file-loading' } },
  { id: 'prefetch', label: 'PREFETCH', track: 'LOAD', tone: 'cyan', startBeat: 5, durationBeats: 4, detail: { type: 'startup-prefetch-start' } },
  { id: 'prefetch-ready', label: 'DATA READY', track: 'SYSTEM', tone: 'cyan', startBeat: 9, durationBeats: 4, detail: { type: 'startup-prefetch-ready', ready: true } },
  { id: 'prewarm', label: 'GPU PREWARM', track: 'SYSTEM', tone: 'amber', startBeat: 13, durationBeats: 3, detail: { type: 'prewarm-start' } },
  { id: 'prewarm-ready', label: 'GPU READY', track: 'SYSTEM', tone: 'white', startBeat: 16, durationBeats: 3, detail: { type: 'prewarm-ready' } },
  { id: 'settle', label: 'SETTLE', track: 'TRANSITION', tone: 'cyan', startBeat: 19, durationBeats: 4, detail: { type: 'startup-settle' } },
  { id: 'launch', label: 'LAUNCH', track: 'TRANSITION', tone: 'white', startBeat: 23, durationBeats: 4, detail: { type: 'playback-start' } },
  { id: 'impact', label: 'IMPACT', track: 'GAMEPLAY', tone: 'red', startBeat: 29, durationBeats: 3, detail: { type: 'damage', intensity: 0.95 } },
  { id: 'seek', label: 'SEEK CUT', track: 'TRANSITION', tone: 'cyan', startBeat: 34, durationBeats: 3, detail: { type: 'seek-start' } },
  { id: 'end', label: 'END', track: 'TRANSITION', tone: 'amber', startBeat: 39, durationBeats: 4, detail: { type: 'playback-end' } },
  { id: 'clear', label: 'CLEAR', track: 'SYSTEM', tone: 'white', startBeat: 44, durationBeats: 1, detail: { type: 'clear' } },
]

type ReplayFxTimelineClipDefinition = {
  id: string
  label: string
  kind: ReplayFxTimelineClipKind
  busId: ReplayFxTimelineBusId
  laneId: string
  tone: ReplayFxTimelineTone
  startBeat: number
  durationBeats: number
  detail?: Omit<ReplayFxEventDetail, 'at' | 'durationMs'>
  phase?: ReplayFxStoryPhase
}

const STORY_CLIP_DEFINITIONS: ReplayFxTimelineClipDefinition[] = [
  ...STORY_PHASE_DEFINITIONS.map((phase, order) => ({
    id: phase.id,
    label: phase.label,
    kind: 'phase' as const,
    busId: 'story' as const,
    laneId: phase.laneId,
    tone: phase.tone,
    startBeat: phase.startBeat,
    durationBeats: phase.durationBeats,
    phase: {
      order,
      minDurationMs: phase.minDurationMs,
      waitFor: phase.waitFor,
    },
  })),
  {
    id: 'story-idle-a',
    label: '06 QUIET WINDOW A',
    kind: 'phase',
    busId: 'story',
    laneId: 'idle',
    tone: 'amber',
    startBeat: 18,
    durationBeats: 4,
    phase: { order: STORY_PHASE_DEFINITIONS.length, minDurationMs: 2000 },
  },
  {
    id: 'story-idle-b',
    label: '07 QUIET WINDOW B',
    kind: 'phase',
    busId: 'story',
    laneId: 'idle',
    tone: 'amber',
    startBeat: 31,
    durationBeats: 3,
    phase: { order: STORY_PHASE_DEFINITIONS.length + 1, minDurationMs: 1600 },
  },
]

const GUIDE_CLIP_DEFINITIONS: ReplayFxTimelineClipDefinition[] = [
  { id: 'app-init', label: 'APP INIT', kind: 'guide', busId: 'runtime', laneId: 'app', tone: 'white', startBeat: 0, durationBeats: 4 },
  { id: 'file-read', label: 'FILE READ', kind: 'guide', busId: 'runtime', laneId: 'file', tone: 'cyan', startBeat: 1, durationBeats: 8 },
  { id: 'world-stream', label: 'WORLD STREAM', kind: 'guide', busId: 'runtime', laneId: 'world', tone: 'cyan', startBeat: 5, durationBeats: 14 },
  { id: 'gpu-runtime', label: 'GPU WARMUP', kind: 'guide', busId: 'runtime', laneId: 'gpu', tone: 'amber', startBeat: 13, durationBeats: 6 },
  { id: 'replay-clock', label: 'REPLAY CLOCK', kind: 'guide', busId: 'runtime', laneId: 'clock', tone: 'white', startBeat: 23, durationBeats: 22 },
  { id: 'app-shell', label: 'APP SHELL', kind: 'guide', busId: 'interface', laneId: 'shell', tone: 'white', startBeat: 2, durationBeats: 21 },
  { id: 'loading-screen', label: 'LOADING SCREEN', kind: 'guide', busId: 'interface', laneId: 'loading-ui', tone: 'cyan', startBeat: 1, durationBeats: 21 },
  { id: 'viewer-reveal', label: 'VIEWER REVEAL', kind: 'guide', busId: 'interface', laneId: 'viewer', tone: 'amber', startBeat: 18, durationBeats: 7 },
  { id: 'replay-hud', label: 'REPLAY HUD', kind: 'guide', busId: 'interface', laneId: 'hud', tone: 'white', startBeat: 22, durationBeats: 23 },
]

const AMBIENT_CLIP_DEFINITIONS: ReplayFxTimelineClipDefinition[] = [
  { id: 'ambient-idle-a', label: 'LOW CRT HUM', kind: 'ambient', busId: 'effects', laneId: 'crt', tone: 'amber', startBeat: 18, durationBeats: 4 },
  { id: 'ambient-idle-b', label: 'SCANLINE DRIFT', kind: 'ambient', busId: 'effects', laneId: 'crt', tone: 'cyan', startBeat: 31, durationBeats: 3 },
]

const EFFECT_CLIP_LAYOUT: Record<string, {
  busId: ReplayFxTimelineBusId
  laneId: string
}> = {
  boot: { busId: 'effects', laneId: 'crt' },
  prefetch: { busId: 'effects', laneId: 'electric' },
  'prefetch-ready': { busId: 'effects', laneId: 'electric' },
  prewarm: { busId: 'effects', laneId: 'electric' },
  'prewarm-ready': { busId: 'effects', laneId: 'electric' },
  settle: { busId: 'effects', laneId: 'transition' },
  launch: { busId: 'effects', laneId: 'transition' },
  impact: { busId: 'gameplay', laneId: 'events' },
  seek: { busId: 'effects', laneId: 'transition' },
  end: { busId: 'effects', laneId: 'transition' },
  clear: { busId: 'effects', laneId: 'crt' },
}

const EFFECT_CLIP_DEFINITIONS: ReplayFxTimelineClipDefinition[] = REPLAY_FX_CUE_DEFINITIONS.map(cue => ({
  id: cue.id,
  label: cue.label,
  kind: 'effect',
  busId: EFFECT_CLIP_LAYOUT[cue.id]!.busId,
  laneId: EFFECT_CLIP_LAYOUT[cue.id]!.laneId,
  tone: cue.tone,
  startBeat: cue.startBeat,
  durationBeats: cue.durationBeats,
  detail: cue.detail,
}))

const TIMELINE_CLIP_DEFINITIONS = [
  ...STORY_CLIP_DEFINITIONS,
  ...GUIDE_CLIP_DEFINITIONS,
  ...AMBIENT_CLIP_DEFINITIONS,
  ...EFFECT_CLIP_DEFINITIONS,
]

export const createDefaultReplayFxTimeline = (
  beatMs = DEFAULT_REPLAY_FX_BEAT_MS
): ReplayFxTimelineCue[] => TIMELINE_CLIP_DEFINITIONS.map(cue => ({
  id: cue.id,
  label: cue.label,
  track: cue.kind === 'effect' ? 'TRANSITION' : 'SYSTEM',
  kind: cue.kind,
  busId: cue.busId,
  laneId: cue.laneId,
  tone: cue.tone,
  startMs: cue.startBeat * beatMs,
  durationMs: cue.durationBeats * beatMs,
  detail: cue.detail ? { ...cue.detail } : undefined,
  phase: cue.phase ? { ...cue.phase } : undefined,
}))

export const getReplayFxTimelineLength = (
  cues: ReplayFxTimelineCue[],
  minimumMs = REPLAY_FX_TIMELINE_LENGTH_BEATS * DEFAULT_REPLAY_FX_BEAT_MS
) => Math.max(
  minimumMs,
  ...cues.map(cue => cue.startMs + cue.durationMs + DEFAULT_REPLAY_FX_BEAT_MS)
)

export const buildReplayFxLiveTimeline = (
  markers: ReplayFxTimelineLiveMarker[],
  currentMs: number,
  activity: ReplayFxTimelineActivity[] = []
): ReplayFxTimelineCue[] => {
  const findMarker = (type: ReplayFxEventType, afterMs = -1) => (
    markers.find(marker => marker.detail.type === type && marker.elapsedMs >= afterMs)
  )
  const fileLoading = findMarker('file-loading')
  const fileReady = findMarker('file-ready')
  const prefetchStart = findMarker('startup-prefetch-start')
  const prefetchReady = findMarker('startup-prefetch-ready')
  const playbackStart = findMarker('playback-start')
  const playbackEnd = findMarker('playback-end')
  const liveStoryPhase = ({
    id,
    label,
    laneId,
    tone,
    startMs,
    endMs,
    phase,
  }: ReplayFxLiveStoryPhaseInput): ReplayFxTimelineCue => ({
    id,
    label,
    track: 'SYSTEM',
    kind: 'measured',
    busId: 'story',
    laneId,
    tone,
    startMs,
    durationMs: Math.max(120, endMs - startMs),
    phase,
  })
  const storyPhases: ReplayFxTimelineCue[] = []

  if (fileLoading) {
    const bootEndMs = Math.min(fileReady?.elapsedMs ?? currentMs, fileLoading.elapsedMs + 900)
    storyPhases.push(
      liveStoryPhase({
        id: 'live-story-boot',
        label: '01 TV POWER ON',
        laneId: 'boot',
        tone: 'white',
        startMs: fileLoading.elapsedMs,
        endMs: bootEndMs,
        phase: { order: 0, minDurationMs: 900 },
      }),
      liveStoryPhase({
        id: 'live-story-file',
        label: '02 FILE INTAKE',
        laneId: 'file',
        tone: 'cyan',
        startMs: fileLoading.elapsedMs,
        endMs: fileReady?.elapsedMs ?? currentMs,
        phase: { order: 1, minDurationMs: 1400, waitFor: 'file-ready' },
      })
    )
  }
  if (fileReady) {
    storyPhases.push(liveStoryPhase({
      id: 'live-story-ui',
      label: '03 UI REVEAL',
      laneId: 'ui-reveal',
      tone: 'amber',
      startMs: fileReady.elapsedMs,
      endMs: playbackStart?.elapsedMs ?? currentMs,
      phase: { order: 2, minDurationMs: 900, waitFor: 'playback-start' },
    }))
  }
  if (prefetchStart) {
    storyPhases.push(liveStoryPhase({
      id: 'live-story-world',
      label: '04 WORLD BUILD',
      laneId: 'world',
      tone: 'cyan',
      startMs: prefetchStart.elapsedMs,
      endMs: prefetchReady?.elapsedMs ?? currentMs,
      phase: { order: 3, minDurationMs: 1800, waitFor: 'startup-prefetch-ready' },
    }))
  }
  if (playbackStart) {
    storyPhases.push(liveStoryPhase({
      id: 'live-story-play',
      label: '05 CONTENT PLAY',
      laneId: 'play',
      tone: 'white',
      startMs: playbackStart.elapsedMs,
      endMs: playbackEnd?.elapsedMs ?? currentMs,
      phase: { order: 4, minDurationMs: 0, waitFor: 'playback-end' },
    }))
  }

  const idleStoryPhases = playbackStart
    ? activity
      .filter(event => event.type === 'low_activity' && (event.duration ?? 0) >= 1000)
      .map((event, index): ReplayFxTimelineCue => {
        const startMs = playbackStart.elapsedMs + event.time
        const durationMs = Math.max(1000, event.duration ?? 1000)
        return {
          id: `live-story-idle-${index}`,
          label: `QUIET ${String(index + 1).padStart(2, '0')}`,
          track: 'SYSTEM',
          kind: 'measured',
          busId: 'story',
          laneId: 'idle',
          tone: 'amber',
          startMs,
          durationMs,
          phase: {
            order: STORY_PHASE_DEFINITIONS.length + index,
            minDurationMs: durationMs,
          },
        }
      })
    : []
  const ambientCues = idleStoryPhases.map((phase, index): ReplayFxTimelineCue => ({
    id: `live-ambient-${index}`,
    label: index % 2 === 0 ? 'LOW CRT HUM' : 'SCANLINE DRIFT',
    track: 'SYSTEM',
    kind: 'ambient',
    busId: 'effects',
    laneId: 'crt',
    tone: index % 2 === 0 ? 'amber' : 'cyan',
    startMs: phase.startMs,
    durationMs: phase.durationMs,
  }))

  const phases = LIVE_PHASE_DEFINITIONS.flatMap((phase): ReplayFxTimelineCue[] => {
    const start = findMarker(phase.start)
    if (!start) return []
    const end = phase.end ? findMarker(phase.end, start.elapsedMs) : undefined
    const durationMs = phase.durationFromWallMs
      ? Math.max(120, start.detail.wallMs ?? start.detail.durationMs ?? 120)
      : Math.max(120, (end?.elapsedMs ?? currentMs) - start.elapsedMs)
    const startMs = phase.durationFromWallMs
      ? Math.max(0, start.elapsedMs - durationMs)
      : start.elapsedMs
    return [{
      id: `live-phase-${phase.id}-${Math.round(start.elapsedMs)}`,
      label: phase.label,
      track: 'SYSTEM',
      kind: 'measured',
      busId: phase.busId,
      laneId: phase.laneId,
      tone: phase.tone,
      startMs,
      durationMs,
    }]
  })

  const events = markers.map((marker, index): ReplayFxTimelineCue => {
    const placement = LIVE_EVENT_PLACEMENT[marker.detail.type]
    const {
      type,
      label,
      intensity,
      packets,
      replayTimeMs,
      wallMs,
      ready,
    } = marker.detail
    return {
      id: `live-event-${marker.detail.type}-${index}`,
      label: placement.label,
      track: marker.detail.type === 'damage' ? 'GAMEPLAY' : 'TRANSITION',
      kind: 'measured',
      busId: placement.busId,
      laneId: placement.laneId,
      tone: placement.tone,
      startMs: marker.elapsedMs,
      durationMs: Math.max(180, marker.detail.durationMs ?? 320),
      detail: {
        type,
        label,
        intensity,
        packets,
        replayTimeMs,
        wallMs,
        ready,
      },
    }
  })

  return [...storyPhases, ...idleStoryPhases, ...phases, ...ambientCues, ...events]
}

const isFiniteNumber = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value)
)

const isValidTimelinePlacement = (
  busId: unknown,
  laneId: unknown
): busId is ReplayFxTimelineBusId => (
  typeof busId === 'string' &&
  typeof laneId === 'string' &&
  REPLAY_FX_TIMELINE_BUSES.some(bus => (
    bus.id === busId &&
    bus.lanes.some(lane => lane.id === laneId)
  ))
)

export const normalizeReplayFxTimeline = (value: unknown): ReplayFxTimelineCue[] => {
  if (!Array.isArray(value)) return createDefaultReplayFxTimeline()

  const defaultsById = new Map(createDefaultReplayFxTimeline().map(cue => [cue.id, cue]))
  const normalized = value.flatMap((candidate): ReplayFxTimelineCue[] => {
    if (!candidate || typeof candidate !== 'object') return []
    const raw = candidate as Partial<ReplayFxTimelineCue>
    const fallback = typeof raw.id === 'string' ? defaultsById.get(raw.id) : undefined
    if (!fallback) return []

    return [{
      ...fallback,
      busId: isValidTimelinePlacement(raw.busId, raw.laneId) ? raw.busId : fallback.busId,
      laneId: isValidTimelinePlacement(raw.busId, raw.laneId) ? raw.laneId! : fallback.laneId,
      startMs: Math.max(0, isFiniteNumber(raw.startMs) ? raw.startMs : fallback.startMs),
      durationMs: Math.max(120, isFiniteNumber(raw.durationMs) ? raw.durationMs : fallback.durationMs),
    }]
  })

  return normalized.length === defaultsById.size
    ? normalized
    : createDefaultReplayFxTimeline()
}

export const updateReplayFxTimelineCue = (
  cues: ReplayFxTimelineCue[],
  id: string,
  update: Partial<Pick<ReplayFxTimelineCue, 'startMs' | 'durationMs' | 'busId' | 'laneId'>>
) => cues.map(cue => {
  if (cue.id !== id) return cue
  const requestedStartMs = isFiniteNumber(update.startMs) ? update.startMs : cue.startMs
  const requestedDurationMs = isFiniteNumber(update.durationMs) ? update.durationMs : cue.durationMs
  return {
    ...cue,
    busId: update.busId ?? cue.busId,
    laneId: update.laneId ?? cue.laneId,
    startMs: Math.max(0, requestedStartMs),
    durationMs: Math.max(120, requestedDurationMs),
    phase: cue.phase
      ? {
        ...cue.phase,
        minDurationMs: cue.kind === 'phase'
          ? Math.max(120, requestedDurationMs)
          : cue.phase.minDurationMs,
      }
      : undefined,
  }
})
